import React, { useRef, useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  motion,
  AnimatePresence,
  useReducedMotion,
  useMotionValue,
  useAnimationFrame,
  useScroll,
  useTransform,
} from 'framer-motion';
import { HOUSE_EASE, PhoneFrame } from './shared';

/* =====================================================================
   Scroll-SAFE mobile showcases.

   CORE INVARIANT: nothing here is a horizontal scroll container. No
   overflow-x:auto/scroll, no scroll-snap-type:x, no -webkit-overflow-
   scrolling, no touch-action:pan-x. The PAGE is the only scroller, so a
   finger landing anywhere on a showcase always scrolls the page. All
   screenshot motion is produced by transform/opacity (timer / rAF / the
   page's own vertical scroll). overflow:hidden is only ever used as a
   CLIP on a fixed-size box, never as a scroller. touch-action:pan-y is
   belt-and-suspenders only.

   Three primitives (themed per app):
     Marquee        - autonomous conveyor (Thrive 1 row, Chess Clock 2 rows)
     CrossfadeStage - timed cross-fade of whole tiles (Sterling)
     DevicePager    - timed in-frame screen swap inside a bezel (Haiku)
     ScrollSwapper  - the page's vertical scroll pans the row (Conduit)
   ===================================================================== */

const wrap = (min, max, v) => {
  const range = max - min;
  return ((((v - min) % range) + range) % range) + min;
};

export function useInView(ref, amount = 0.05) {
  const [inView, setInView] = useState(true);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: amount });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, amount]);
  return inView;
}

/* timer-driven active index, gated by inView + paused + reduced motion +
   document visibility (so it never runs offscreen or in a background tab) */
export function useTimedIndex({ count, dwell, inView, paused, reduce }) {
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (reduce || paused || !inView || count <= 1) return undefined;
    const id = setInterval(() => {
      if (!document.hidden) setActive((a) => (a + 1) % count);
    }, dwell);
    return () => clearInterval(id);
  }, [reduce, paused, inView, dwell, count]);
  const go = useCallback((i) => setActive(((i % count) + count) % count), [count]);
  return [active, go, setActive];
}

/* ----------------------------- shared controls ----------------------------- */
export const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-top: 14px;
`;

export const Dots = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
`;

export const Dot = styled.button`
  width: 9px;
  height: 9px;
  padding: 0;
  border: none;
  border-radius: var(--radius-pill);
  background: ${(p) => p.$accent};
  opacity: ${(p) => (p.$on ? 1 : 0.32)};
  transform: scale(${(p) => (p.$on ? 1.25 : 0.85)});
  transition: transform 0.24s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.24s ease;
  cursor: pointer;
  position: relative;
  &::after { content: ''; position: absolute; inset: -16px; }
`;

const PauseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  /* the global button rule pads 0.75rem 1.5rem, which crushes the glyph */
  padding: 0;
  border-radius: var(--radius-pill);
  border: 1px solid ${(p) => p.$border};
  background: transparent;
  color: ${(p) => p.$accent};
  cursor: pointer;
  line-height: 0;
  svg { width: 12px; height: 12px; }
`;

const PlayIcon = () => (
  <svg viewBox="0 0 12 12" fill="currentColor" aria-hidden="true"><path d="M2.5 1.5v9l8-4.5z" /></svg>
);
const PauseIcon = () => (
  <svg viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
    <rect x="2.5" y="1.5" width="2.6" height="9" rx="0.6" />
    <rect x="6.9" y="1.5" width="2.6" height="9" rx="0.6" />
  </svg>
);

export function PauseToggle({ paused, setPaused, accent, border }) {
  return (
    <PauseButton
      type="button"
      $accent={accent}
      $border={border}
      aria-pressed={paused}
      aria-label={paused ? 'Play screenshot animation' : 'Pause screenshot animation'}
      onClick={() => setPaused((p) => !p)}
    >
      {paused ? <PlayIcon /> : <PauseIcon />}
    </PauseButton>
  );
}

/* reduced-motion / no-JS fallback: a static 2-col grid, everything visible,
   no horizontal scroller */
const StaticGridEl = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  max-width: 520px;
  margin: 0 auto;

  figure {
    margin: 0;
    aspect-ratio: 1284 / 2778;
    border-radius: 22px;
    overflow: hidden;
    background: ${(p) => p.$cardBg || '#000'};
    border: 1px solid ${(p) => p.$border};
    box-shadow: 0 14px 36px rgba(20, 18, 12, 0.14);
  }
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
`;

function StaticTiles({ items, theme, label }) {
  return (
    <StaticGridEl $cardBg={theme.cardBg} $border={theme.border} role="group" aria-label={label}>
      {items.map((img) => (
        <figure key={img.src}>
          <img src={img.src} alt={img.alt} loading="lazy" decoding="async" />
        </figure>
      ))}
    </StaticGridEl>
  );
}

/* =============================== Marquee =============================== */
const MarqueeStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Mask = styled.div`
  position: relative;
  overflow: hidden; /* CLIP only - not a scroller */
  touch-action: pan-y;
  -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%);
  mask-image: linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%);
`;

const MTrack = styled(motion.div)`
  display: flex;
  gap: 16px;
  width: max-content;
  padding: 6px 0;
  will-change: transform;
`;

const MTile = styled.figure`
  flex: 0 0 auto;
  margin: 0;
  width: clamp(196px, 62vw, 248px);
  aspect-ratio: 1284 / 2778;
  border-radius: 26px;
  overflow: hidden;
  background: ${(p) => p.$cardBg || '#000'};
  border: 1px solid ${(p) => p.$border};
  box-shadow: 0 16px 40px rgba(20, 18, 12, 0.16);
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
`;

function MarqueeRow({ items, theme, velocity, direction, paused, inView, reduce }) {
  const x = useMotionValue(0);
  const trackRef = useRef(null);
  const halfRef = useRef(0);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    const measure = () => { halfRef.current = track.scrollWidth / 2; };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(track);
    const imgs = [...track.querySelectorAll('img')];
    imgs.forEach((img) => { if (!img.complete) img.addEventListener('load', measure, { once: true }); });
    const t = setTimeout(measure, 500);
    return () => { ro.disconnect(); clearTimeout(t); };
  }, []);

  useAnimationFrame((_, delta) => {
    if (reduce || paused || !inView || document.hidden || !halfRef.current) return;
    const moveBy = direction * velocity * (delta / 1000);
    x.set(wrap(-halfRef.current, 0, x.get() + moveBy));
  });

  return (
    <Mask>
      <MTrack ref={trackRef} style={{ x }}>
        {[...items, ...items].map((img, i) => (
          <MTile key={`${img.src}-${i}`} $cardBg={theme.cardBg} $border={theme.border} aria-hidden={i >= items.length ? 'true' : undefined}>
            <img src={img.src} alt={i < items.length ? img.alt : ''} loading="lazy" decoding="async" />
          </MTile>
        ))}
      </MTrack>
    </Mask>
  );
}

export function Marquee({ items, theme, label, velocity = 14, direction = -1, rows = 1 }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref);
  const [paused, setPaused] = useState(false);

  if (reduce) return <StaticTiles items={items} theme={theme} label={label} />;

  const rowData =
    rows === 2
      ? [
          { items: items.slice(0, Math.ceil(items.length / 2)), dir: -1 },
          { items: items.slice(Math.ceil(items.length / 2)), dir: 1 },
        ]
      : [{ items, dir: direction }];

  return (
    <div ref={ref}>
      <MarqueeStack role="group" aria-roledescription="carousel" aria-label={label}>
        {rowData.map((r, i) => (
          <MarqueeRow
            key={i}
            items={r.items}
            theme={theme}
            velocity={velocity}
            direction={r.dir}
            paused={paused}
            inView={inView}
            reduce={reduce}
          />
        ))}
      </MarqueeStack>
      <Controls>
        <PauseToggle paused={paused} setPaused={setPaused} accent={theme.accent} border={theme.border} />
      </Controls>
    </div>
  );
}

/* ============================ CrossfadeStage ============================ */
const Stage = styled.div`
  position: relative;
  width: clamp(224px, 76vw, 300px);
  margin: 0 auto;
  aspect-ratio: 1284 / 2778;
  border-radius: 28px;
  overflow: hidden; /* CLIP only */
  touch-action: pan-y;
  background: ${(p) => p.$cardBg || '#000'};
  border: 1px solid ${(p) => p.$border};
  box-shadow: 0 24px 56px rgba(20, 18, 12, 0.22), 0 6px 16px rgba(20, 18, 12, 0.12);
`;

const CrossLayer = styled(motion.div)`
  position: absolute;
  inset: 0;
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
`;

export function CrossfadeStage({ items, theme, label, dwell = 5000 }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref);
  const [paused, setPaused] = useState(false);
  const [active, go] = useTimedIndex({ count: items.length, dwell, inView, paused, reduce });
  const current = items[active] || items[0];

  return (
    <div ref={ref}>
      <Stage $cardBg={theme.cardBg} $border={theme.border} role="group" aria-roledescription="carousel" aria-label={label} aria-live="polite">
        <AnimatePresence mode="popLayout" initial={false}>
          <CrossLayer
            key={current.src}
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduce ? 0 : 0.85, ease: HOUSE_EASE }}
          >
            <img src={current.src} alt={current.alt} loading="eager" decoding="async" />
          </CrossLayer>
        </AnimatePresence>
      </Stage>
      <Controls>
        <Dots role="group" aria-label={`${label} pagination`}>
          {items.map((img, i) => (
            <Dot
              key={img.src}
              type="button"
              $accent={theme.accent}
              $on={i === active}
              aria-current={i === active}
              aria-label={`Go to screen ${i + 1} of ${items.length}`}
              onClick={() => go(i)}
            />
          ))}
        </Dots>
        {!reduce && <PauseToggle paused={paused} setPaused={setPaused} accent={theme.accent} border={theme.border} />}
      </Controls>
    </div>
  );
}

/* ============================= DevicePager (Haiku) ============================= */
const PagerStage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PagerDevice = styled(motion.div)`
  width: clamp(230px, 74vw, 290px);
`;

const ScreenLayer = styled(motion.div)`
  position: absolute;
  inset: 0;
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
`;

const PagerCaption = styled.div`
  position: relative;
  width: min(340px, 86vw);
  min-height: 140px;
  margin-top: 1.5rem;
  text-align: center;
`;

const PagerCaptionInner = styled(motion.div)`
  position: absolute;
  inset: 0;
`;

const PagerKicker = styled.div`
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  font-weight: 600;
  color: ${(p) => p.$accent};
  margin-bottom: 0.55rem;
`;

const PagerText = styled.div`
  font-family: ${(p) => p.$font};
  font-size: 1.2rem;
  line-height: 1.35;
  color: ${(p) => p.$ink};
`;

export function DevicePager({ screens, theme, label }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref);
  const [paused, setPaused] = useState(false);
  const [active, go] = useTimedIndex({ count: screens.length, dwell: 4800, inView, paused, reduce });
  const current = screens[active] || screens[0];

  return (
    <PagerStage ref={ref}>
      <PagerDevice
        initial={{ opacity: 0, y: 22 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.55, ease: HOUSE_EASE }}
      >
        <PhoneFrame $bezel={theme.bezel}>
          <div className="screen" style={{ touchAction: 'pan-y' }}>
            <AnimatePresence mode="popLayout" initial={false}>
              <ScreenLayer
                key={current.src}
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.99 }}
                transition={{ duration: reduce ? 0 : 0.7, ease: HOUSE_EASE }}
              >
                <img src={current.src} alt={current.alt} loading="eager" decoding="async" />
              </ScreenLayer>
            </AnimatePresence>
          </div>
        </PhoneFrame>
      </PagerDevice>

      <PagerCaption aria-live="polite">
        <AnimatePresence mode="wait" initial={false}>
          <PagerCaptionInner
            key={active}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: reduce ? 0 : 0.4, ease: HOUSE_EASE }}
          >
            <PagerKicker $accent={theme.accent}>{current.label}</PagerKicker>
            <PagerText $font={theme.titleFont} $ink={theme.ink}>{current.caption}</PagerText>
          </PagerCaptionInner>
        </AnimatePresence>
      </PagerCaption>

      <Controls>
        <Dots role="group" aria-label={`${label} pagination`}>
          {screens.map((s, i) => (
            <Dot
              key={s.src}
              type="button"
              $accent={theme.accent}
              $on={i === active}
              aria-current={i === active}
              aria-label={`Go to screen ${i + 1} of ${screens.length}`}
              onClick={() => go(i)}
            />
          ))}
        </Dots>
        {!reduce && <PauseToggle paused={paused} setPaused={setPaused} accent={theme.accent} border={theme.border} />}
      </Controls>
    </PagerStage>
  );
}

/* ============================ ScrollSwapper (Conduit) ============================ */
/* The user's own vertical page scroll pans the row sideways. No scroller,
   no autoplay - the scroll IS the input, so there is zero gesture conflict. */
const Swapper = styled.div`
  position: relative;
  overflow: hidden; /* CLIP only - the row is wider than this box */
`;

const SwapRow = styled(motion.div)`
  display: flex;
  gap: 16px;
  width: max-content;
  padding: 6px 0;
  will-change: transform;
`;

const SwapTile = styled.figure`
  flex: 0 0 auto;
  margin: 0;
  width: clamp(196px, 62vw, 250px);
  aspect-ratio: 1284 / 2778;
  border-radius: 26px;
  overflow: hidden;
  background: ${(p) => p.$cardBg || '#000'};
  border: 1px solid ${(p) => p.$border};
  box-shadow: 0 16px 40px rgba(20, 18, 12, 0.16);
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
`;

export function ScrollSwapper({ items, theme, label }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const rowRef = useRef(null);
  const [travel, setTravel] = useState(0);

  useEffect(() => {
    const measure = () => {
      if (rowRef.current && ref.current) {
        setTravel(Math.max(0, rowRef.current.scrollWidth - ref.current.clientWidth));
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (rowRef.current) ro.observe(rowRef.current);
    if (ref.current) ro.observe(ref.current);
    const t = setTimeout(measure, 500);
    return () => { ro.disconnect(); clearTimeout(t); };
  }, []);

  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  // pan only across the middle of the pass so the first/last tiles rest fully visible
  const x = useTransform(scrollYProgress, [0.1, 0.9], [0, -travel]);

  if (reduce) return <StaticTiles items={items} theme={theme} label={label} />;

  return (
    <Swapper ref={ref} role="group" aria-label={label}>
      <SwapRow ref={rowRef} style={{ x }}>
        {items.map((img) => (
          <SwapTile key={img.src} $cardBg={theme.cardBg} $border={theme.border}>
            <img src={img.src} alt={img.alt} loading="lazy" decoding="async" />
          </SwapTile>
        ))}
      </SwapRow>
    </Swapper>
  );
}
